/**
 * `annotations.tsx` — the two **non-graph** React Flow node types (interactive-diagrams v0.4):
 *   - `StickyNode` — a free-floating sticky note (title-less, editable text, resizable),
 *   - `LaneNode`   — a swimlane / frame (labeled header band, resizable, sits behind the graph).
 *
 * Neither is part of the mermaid graph — they carry no handles and no edges. They live entirely
 * in the `%% @aletheia` sidecar (`SidecarData.notes` / `.lanes`), so a vanilla mermaid renderer
 * drops them and the flowchart still renders (the plan's portability contract). Colors are
 * carried as portable hexes and applied through a `--dg-accent` custom property so the note/lane
 * chrome still reads correctly across every app theme.
 *
 * Editing mirrors `MermaidNode`: the text/label shows as static content; double-click swaps in an
 * input; blur / Enter (Escape to cancel) commits. Moves + resizes commit through the canvas.
 */

import { useEffect, useRef, useState } from 'react';
import { NodeResizer, type NodeProps, type Node } from '@xyflow/react';
import type { StickyNote, Swimlane } from '../model';

/** Default accent when a note/lane carries no explicit color. */
export const STICKY_DEFAULT = '#f59e0b';
export const LANE_DEFAULT = '#3b82f6';
/** Default footprint for a freshly-added note / lane. */
export const STICKY_DEFAULT_SIZE: [number, number] = [176, 132];
export const LANE_DEFAULT_SIZE: [number, number] = [320, 260];

// ── Sticky note ────────────────────────────────────────────────────────────────

export type StickyNodeData = {
  note: StickyNote;
  onEditText?: (id: string, text: string) => void;
  onResizeEnd?: (id: string, w: number, h: number) => void;
  [k: string]: unknown;
};
export type StickyFlowNode = Node<StickyNodeData, 'sticky'>;

export function StickyNode({ data, selected }: NodeProps<StickyFlowNode>): JSX.Element {
  const { note, onEditText, onResizeEnd } = data;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.text);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (onEditText && draft !== note.text) onEditText(note.id, draft);
  };

  const accent = note.color ?? STICKY_DEFAULT;
  return (
    <div className={`dg-sticky${selected ? ' is-selected' : ''}`} style={{ ['--dg-accent' as string]: accent }}>
      {onResizeEnd && (
        <NodeResizer
          isVisible={!!selected}
          minWidth={96}
          minHeight={72}
          lineClassName="diagram-resize-line"
          handleClassName="diagram-resize-handle"
          onResizeEnd={(_e, p) => onResizeEnd(note.id, Math.round(p.width), Math.round(p.height))}
        />
      )}
      {editing ? (
        <textarea
          ref={ref}
          className="dg-sticky-text nodrag nopan"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') {
              setDraft(note.text);
              setEditing(false);
            } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit();
          }}
          onBlur={commit}
        />
      ) : (
        <div
          className="dg-sticky-text"
          onDoubleClick={() => {
            if (!onEditText) return;
            setDraft(note.text);
            setEditing(true);
          }}
        >
          {note.text || <span className="dg-sticky-placeholder">Double-click to edit…</span>}
        </div>
      )}
    </div>
  );
}

// ── Subgraph frame (structural — derived from mermaid `subgraph … end`) ────────

/** Default frame accent when none is assigned. */
export const SUBGRAPH_ACCENT = '#64748b';

export type SubgraphNodeData = {
  title: string;
  /** Portable accent hex for the frame border/tint/header. */
  accent?: string;
  [k: string]: unknown;
};
export type SubgraphFlowNode = Node<SubgraphNodeData, 'subgraph'>;

/**
 * A read-only container frame for a mermaid `subgraph` — drawn BEHIND the graph so the canvas
 * shows the same grouping the vanilla mermaid render does. Purely visual: no handles, no edit,
 * no selection (the whole node is `pointer-events:none` via CSS so it never intercepts a drag or
 * box-select). Its position + size are computed by the canvas from its members' bounding box.
 */
export function SubgraphNode({ data }: NodeProps<SubgraphFlowNode>): JSX.Element {
  return (
    <div className="dg-subgraph" style={{ ['--dg-accent' as string]: data.accent ?? SUBGRAPH_ACCENT }}>
      <div className="dg-subgraph-header">{data.title}</div>
    </div>
  );
}

// ── Swimlane / frame ─────────────────────────────────────────────────────────

export type LaneNodeData = {
  lane: Swimlane;
  onEditLabel?: (id: string, label: string) => void;
  onResizeEnd?: (id: string, w: number, h: number) => void;
  [k: string]: unknown;
};
export type LaneFlowNode = Node<LaneNodeData, 'lane'>;

export function LaneNode({ data, selected }: NodeProps<LaneFlowNode>): JSX.Element {
  const { lane, onEditLabel, onResizeEnd } = data;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(lane.label);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) ref.current?.select();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (onEditLabel && draft !== lane.label) onEditLabel(lane.id, draft);
  };

  const accent = lane.color ?? LANE_DEFAULT;
  const vertical = lane.orientation === 'vertical';
  return (
    <div
      className={`dg-lane${vertical ? ' is-vertical' : ''}${selected ? ' is-selected' : ''}`}
      style={{ ['--dg-accent' as string]: accent }}
    >
      {onResizeEnd && (
        <NodeResizer
          isVisible={!!selected}
          minWidth={140}
          minHeight={120}
          lineClassName="diagram-resize-line"
          handleClassName="diagram-resize-handle"
          onResizeEnd={(_e, p) => onResizeEnd(lane.id, Math.round(p.width), Math.round(p.height))}
        />
      )}
      <div className="dg-lane-header">
        {editing ? (
          <input
            ref={ref}
            className="dg-lane-input nodrag nopan"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commit();
              else if (e.key === 'Escape') {
                setDraft(lane.label);
                setEditing(false);
              }
            }}
            onBlur={commit}
          />
        ) : (
          <span
            className="dg-lane-title"
            onDoubleClick={() => {
              if (!onEditLabel) return;
              setDraft(lane.label);
              setEditing(true);
            }}
          >
            {lane.label || 'Lane'}
          </span>
        )}
      </div>
    </div>
  );
}
